import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ERROR_CODES } from '@icebreaker/shared';
import type {
  ApiErrorBody,
  PaginatedResult,
  PanelCreateOrganizationInput,
  PanelOrganizationDetailDto,
  PanelOrganizationListQuery,
  PanelOrganizationRowDto,
  PanelOwnerInvitationDto,
  PanelProvisionedOrganizationDto,
} from '@icebreaker/shared';
import { createAsyncActions } from '@icebreaker/shared/store';

/**
 * KURUMLAR DEPOSU (kurum satışla açılır · Story C1, C2).
 *
 * Mutasyon sonrası liste ve detay SUNUCUDAN tazelenir (`INVALIDATION_MAP`
 * `panelOrgs/*`): ikinci bir personel aynı kurumda aynı anda işlem yapmış
 * olabilir, satır yerinde uydurulmaz.
 */
export const fetchOrganizations = createAsyncActions<
  PanelOrganizationListQuery,
  PaginatedResult<PanelOrganizationRowDto>
>('panelOrgs/listFetch');
export const fetchOrganization = createAsyncActions<{ id: string }, PanelOrganizationDetailDto>(
  'panelOrgs/detailFetch',
);
export const provisionOrganization = createAsyncActions<
  PanelCreateOrganizationInput,
  PanelProvisionedOrganizationDto
>('panelOrgs/provision');
export const resendOwnerInvitation = createAsyncActions<
  { organizationId: string },
  { organizationId: string; invitation: PanelOwnerInvitationDto }
>('panelOrgs/ownerResend');
/**
 * SAHİP ADRESİ ÖN KONTROLÜ (KH8 · Story H7 · TP1). Yanıt sorulan adresi geri
 * taşır: alan bu arada değiştiyse eski adresin sonucu yeni adrese yazılmaz.
 */
export const checkOwnerEmail = createAsyncActions<
  { ownerEmail: string },
  { ownerEmail: string; emailInPersonalAccount: boolean }
>('panelOrgs/ownerEmailCheckFetch');
export const replaceOwnerInvitation = createAsyncActions<
  { organizationId: string; ownerEmail: string; revokeFirst: boolean },
  { organizationId: string; invitation: PanelOwnerInvitationDto }
>('panelOrgs/ownerReplace');

/**
 * KAPAĞI KALDIR (kurum kapağı · Story P1). Gövde personelin gördüğü kapağın
 * kimliğini taşır; yanıt sunucunun güncel detayıdır ve detayın yerine yazılır.
 */
export const removeOrganizationCover = createAsyncActions<
  { organizationId: string; coverMediaId: string; reason: string },
  PanelOrganizationDetailDto
>('panelOrgs/coverRemove');

/**
 * Kapak kartının tek satırlık sonucu. `changed` / `notSet`: sunucu 409 döndü
 * (kapak bu arada değişti ya da zaten kaldırılmış), detay yeniden çekildi.
 */
export type CoverNotice = 'removed' | 'changed' | 'notSet';

function coverNoticeFor(error: ApiErrorBody | null | undefined): CoverNotice | null {
  if (error?.code === ERROR_CODES.ORGANIZATION_COVER_CHANGED) return 'changed';
  if (error?.code === ERROR_CODES.ORGANIZATION_COVER_NOT_SET) return 'notSet';
  return null;
}

export interface OrganizationFilters {
  readonly q: string;
  readonly page: number;
}

type Phase = 'idle' | 'loading' | 'succeeded' | 'failed';

export type OwnerNotice = 'resent' | 'replaced';

export interface OrganizationsState {
  list: {
    items: PanelOrganizationRowDto[];
    total: number;
    page: number;
    pageSize: number;
    hasNext: boolean;
    phase: Phase;
    error: ApiErrorBody | null;
  };
  filters: OrganizationFilters;
  detail: {
    id: string | null;
    data: PanelOrganizationDetailDto | null;
    phase: Phase;
    error: ApiErrorBody | null;
  };
  provision: {
    phase: Phase;
    error: ApiErrorBody | null;
    result: PanelProvisionedOrganizationDto | null;
  };
  ownerAction: {
    kind: 'resend' | 'replace' | null;
    phase: Phase;
    error: ApiErrorBody | null;
    notice: OwnerNotice | null;
  };
  coverRemoval: {
    phase: Phase;
    error: ApiErrorBody | null;
    notice: CoverNotice | null;
  };
  /** Son sorulan sahip adresi ve sonucu (`personal` yalnız `succeeded` iken anlamlı). */
  ownerEmailCheck: {
    email: string | null;
    phase: Phase;
    personal: boolean;
  };
}

const initialState: OrganizationsState = {
  list: {
    items: [],
    total: 0,
    page: 1,
    pageSize: 0,
    hasNext: false,
    phase: 'idle',
    error: null,
  },
  filters: { q: '', page: 1 },
  detail: { id: null, data: null, phase: 'idle', error: null },
  provision: { phase: 'idle', error: null, result: null },
  ownerAction: { kind: null, phase: 'idle', error: null, notice: null },
  coverRemoval: { phase: 'idle', error: null, notice: null },
  ownerEmailCheck: { email: null, phase: 'idle', personal: false },
};

const slice = createSlice({
  name: 'panelOrgs',
  initialState,
  reducers: {
    filterChanged(state, action: PayloadAction<Partial<OrganizationFilters>>) {
      const next = { ...state.filters, ...action.payload };
      state.filters = action.payload.page === undefined ? { ...next, page: 1 } : next;
    },
    /** Diyalog açılıp kapanırken önceki denemenin sonucu ve hatası taşınmaz. */
    provisionReset(state) {
      state.provision = { phase: 'idle', error: null, result: null };
    },
    ownerActionReset(state) {
      state.ownerAction = { kind: null, phase: 'idle', error: null, notice: null };
    },
    /** Diyalog açılırken ve kurum değişirken önceki denemenin sonucu taşınmaz. */
    coverRemovalReset(state) {
      state.coverRemoval = { phase: 'idle', error: null, notice: null };
    },
    ownerEmailCheckReset(state) {
      state.ownerEmailCheck = { email: null, phase: 'idle', personal: false };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizations.request, (state) => {
        state.list.phase = 'loading';
        state.list.error = null;
      })
      .addCase(fetchOrganizations.success, (state, action) => {
        state.list.phase = 'succeeded';
        state.list.items = action.payload.items.map((item) => ({ ...item }));
        state.list.total = action.payload.total;
        state.list.page = action.payload.page;
        state.list.pageSize = action.payload.pageSize;
        state.list.hasNext = action.payload.hasNext;
      })
      .addCase(fetchOrganizations.failure, (state, action) => {
        state.list.phase = 'failed';
        state.list.error = action.payload;
      })
      .addCase(fetchOrganization.request, (state, action) => {
        if (state.detail.id !== action.payload.id) state.detail.data = null;
        state.detail.id = action.payload.id;
        state.detail.phase = 'loading';
        state.detail.error = null;
      })
      .addCase(fetchOrganization.success, (state, action) => {
        if (state.detail.id !== action.payload.id) return;
        state.detail.phase = 'succeeded';
        state.detail.data = action.payload;
      })
      .addCase(fetchOrganization.failure, (state, action) => {
        state.detail.phase = 'failed';
        state.detail.error = action.payload;
      })
      .addCase(provisionOrganization.request, (state) => {
        state.provision = { phase: 'loading', error: null, result: null };
      })
      .addCase(provisionOrganization.success, (state, action) => {
        state.provision = { phase: 'succeeded', error: null, result: action.payload };
      })
      .addCase(provisionOrganization.failure, (state, action) => {
        state.provision = { phase: 'failed', error: action.payload, result: null };
      })
      .addCase(resendOwnerInvitation.request, (state) => {
        state.ownerAction = { kind: 'resend', phase: 'loading', error: null, notice: null };
      })
      .addCase(resendOwnerInvitation.success, (state) => {
        state.ownerAction = { kind: 'resend', phase: 'succeeded', error: null, notice: 'resent' };
      })
      .addCase(resendOwnerInvitation.failure, (state, action) => {
        state.ownerAction = {
          kind: 'resend',
          phase: 'failed',
          error: action.payload,
          notice: null,
        };
      })
      .addCase(checkOwnerEmail.request, (state, action) => {
        state.ownerEmailCheck = {
          email: action.payload.ownerEmail,
          phase: 'loading',
          personal: false,
        };
      })
      .addCase(checkOwnerEmail.success, (state, action) => {
        if (state.ownerEmailCheck.email !== action.payload.ownerEmail) return;
        state.ownerEmailCheck.phase = 'succeeded';
        state.ownerEmailCheck.personal = action.payload.emailInPersonalAccount;
      })
      .addCase(checkOwnerEmail.failure, (state) => {
        state.ownerEmailCheck.phase = 'failed';
        state.ownerEmailCheck.personal = false;
      })
      .addCase(removeOrganizationCover.request, (state) => {
        state.coverRemoval = { phase: 'loading', error: null, notice: null };
      })
      .addCase(removeOrganizationCover.success, (state, action) => {
        state.coverRemoval = { phase: 'succeeded', error: null, notice: 'removed' };
        if (state.detail.id === action.payload.id) state.detail.data = action.payload;
      })
      .addCase(removeOrganizationCover.failure, (state, action) => {
        state.coverRemoval = {
          phase: 'failed',
          error: action.payload,
          notice: coverNoticeFor(action.payload),
        };
      })
      .addCase(replaceOwnerInvitation.request, (state) => {
        state.ownerAction = { kind: 'replace', phase: 'loading', error: null, notice: null };
      })
      .addCase(replaceOwnerInvitation.success, (state) => {
        state.ownerAction = {
          kind: 'replace',
          phase: 'succeeded',
          error: null,
          notice: 'replaced',
        };
      })
      .addCase(replaceOwnerInvitation.failure, (state, action) => {
        state.ownerAction = {
          kind: 'replace',
          phase: 'failed',
          error: action.payload,
          notice: null,
        };
      });
  },
});

export const {
  filterChanged: organizationFilterChanged,
  provisionReset,
  ownerActionReset,
  coverRemovalReset,
  ownerEmailCheckReset,
} = slice.actions;
export const organizationsReducer = slice.reducer;
